import React from "react";
import { useTranslation } from "react-i18next";
import DigitalTwinTabs from "../../PagesContent/DigitalTwinV3/DigitalTwinTabs";
import DigitalTwinHeader from "../../PagesContent/DigitalTwinV3/DigitalTwinHeader";
import PrimaryTabPanel from "../../Features/Tabs/PrimaryTabPanel";
import { decryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import BatteryVitals from "../../PagesContent/DigitalTwinV3/Tabs/BatteryVitals/BatteryVitals";
import BatteryAlerts from "../../PagesContent/DigitalTwinV3/Tabs/BatteryAlerts/BatteryAlerts";
import BatteryHealth from "../../PagesContent/DigitalTwinV3/Tabs/BatteryHealth/BatteryHealth";
import BatteryHealthV2 from "../../PagesContent/DigitalTwinV3/Tabs/BatteryHealth/BatteryHealthV2";
import { Box } from "@mui/material";
import BatteryOverview from "../../PagesContent/DigitalTwinV3/Tabs/BatteryOverview/BatteryOverview";
import VehicleVitals from "../../PagesContent/DigitalTwinV3/Tabs/VehicleVitals/VehicleVitals";
import DeviceHealth from "../../PagesContent/DigitalTwinV3/Tabs/DeviceHealth/DeviceHealth";
import BatteryPerformance from "../../PagesContent/DigitalTwinV3/Tabs/BatteryPerformance/BatteryPerformance";
import Components from "../../PagesContent/DigitalTwinV3/Tabs/Components/Components";
import { usePermissions } from "../../Context/PermissionContext";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import { getAvailableAlertTabs } from "../../PagesContent/DigitalTwinV3/Tabs/BatteryAlerts/alertTabsConfig";
import { useBatteryInfo } from "./DigitalTwinContext";
import { getComponentsTabLabel } from "../../Helper/DigitalTwin/BatteryComponentsHelper";

const getActiveTab = (queryParams, clientTabs) => {
	let currentTabValue = queryParams.tabValue
	if (clientTabs && !clientTabs.includes(currentTabValue)) {
		return clientTabs[0];
	}
	const allTabs = ['overview', 'batteryVitals', 'alerts', 'vehicleVitals', 'performance', 'health', 'batteryHealth', 'logs', 'warranty', 'passport', 'deviceHealth', 'components'];

	if (currentTabValue && allTabs.includes(currentTabValue)) {
		return currentTabValue;
	}

	return allTabs[0];
}


function DigitalTwinMain({
	pagesContent,
	batteryID,
}) {

	const { t } = useTranslation(['digital_twin'])
	const queryParams = decryptTheParams();
	const { hasPermission, isRbacV2Enabled } = usePermissions();
	// batteryConfig is populated before DigitalTwinMain mounts (DigitalTwin.jsx gates on
	// batteryInfoFetched), so the label resolves on first render - no flicker.
	const { showBusDeviceInfo } = useBatteryInfo();

	const hasBatteryIntelligence = isRbacV2Enabled()
		? hasPermission(FEATURE_CODES.BATTERY_INTELLIGENCE, 'view')
		: true;

	// Components tab is gated both by orgMeta.tabsToShow (config) and by the
	// BATTERY_COMPONENTS view permission. V1 orgs fall back to true.
	const hasBatteryComponents = isRbacV2Enabled()
		? hasPermission(FEATURE_CODES.BATTERY_COMPONENTS, 'view')
		: true;

	// Hide the top-level Alerts tab when the user can see none of its sub-tabs.
	// Reuse the same resolver BatteryAlerts uses so the two stay in sync. In V1
	// hasPermission() returns true (fallback), so the tab is kept for legacy orgs.
	const enabledFeatures = pagesContent?.metaInfo?.enabledFeatures || [];
	const availableAlertTabs = getAvailableAlertTabs(enabledFeatures, {
		canViewPlatformAlerts: hasPermission(FEATURE_CODES.PLATFORM_ALERTS_V2, 'view'),
		canViewBmsFaults: hasPermission(FEATURE_CODES.BMS_FAULTS_V2, 'view'),
	});
	const hasAnyAlertTab = availableAlertTabs.length > 0;

	let clientTabs = pagesContent.metaInfo.tabsToShow || []
	const [selectedTab, setSelectedTab] = React.useState(getActiveTab(queryParams, clientTabs));

	const allTabs = [
		{
			id: 0,
			name: t('digital_twin:battery_overview', "Overview"),
			slug: "overview",
			disabled: false,
		},
		{
			id: 1,
			name: t('digital_twin:battery_vitals', "Battery Vitals"),
			slug: "batteryVitals",
			disabled: false,
		},
		{
			id: 2,
			name: t('digital_twin:vehicle_vitals', "Vehicle Vitals"),
			slug: "vehicleVitals",
			disabled: false,
		},
		{
			id: 3,
			name: t('digital_twin:alerts', "Alerts"),
			slug: "alerts",
			disabled: !hasAnyAlertTab,
		},
		{
			id: 4,
			name: t('digital_twin:performance', "Performance"),
			slug: "performance",
			disabled: false,
		},
		{
			id: 5,
			name: t('digital_twin:battery_health', "Battery Health"),
			slug: "health",
			// Visibility is orgMeta's call via tabsToShow (Vammo lists `health`).
			// `disabled` here is only for permission gates - see batteryHealth/components
			// below - so a hardcoded flag must not override org config.
			disabled: false,
		},
		{
			id: 7,
			name: t('digital_twin:battery_health', "Battery Health"),
			slug: "batteryHealth",
			disabled: !hasBatteryIntelligence,
		},
		// Components sits before Device Health: tabsToShow is filtered out of this array,
		// so the order here is the order on screen. ids are identity, not position, and
		// are deliberately left alone - orgMeta tabsToShow keys off slug.
		{
			id: 8,
			name: getComponentsTabLabel(showBusDeviceInfo(), t),
			slug: "components",
			disabled: !hasBatteryComponents,
		},
		{
			id: 6,
			name: t('digital_twin:device_health', "Device Health"),
			slug: "deviceHealth",
			disabled: false,
		},
	];

	// Exclude disabled tabs — DigitalTwinTabs renders every tab as clickable and
	// ignores `disabled`, so a permission-gated tab (e.g. batteryHealth without
	// BATTERY_INTELLIGENCE) or the deprecated `health` tab must be filtered out here.
	let tabsToShow = allTabs.filter((tab) => !tab.disabled)

	if (clientTabs) {
		tabsToShow = allTabs.filter((tab) => clientTabs.includes(tab.slug) && !tab.disabled)
	}

	return (
		<div style={{ height: '100vh', paddingTop: '14px', paddingLeft: '16px', paddingRight: '16px' }}>
			<DigitalTwinHeader />

			<Box sx={{
				position: 'sticky',
				top: '50px',
				backgroundColor: '#FAFAFA',
				marginLeft: '-16px',
				marginRight: '-16px',
				zIndex: (theme) => theme.zIndex.appBar + 1
			}}>
				<DigitalTwinTabs
					tabs={tabsToShow}
					changeTheTab={(tabId) => { setSelectedTab(tabId) }}
					selectedTab={selectedTab}
				/>
			</Box>

			<PrimaryTabPanel value={selectedTab} id='overview' key={0} >
				<BatteryOverview batteryID={batteryID} />
			</PrimaryTabPanel>

			<PrimaryTabPanel value={selectedTab} id='batteryVitals' key={1} >
				<BatteryVitals batteryID={batteryID} />
			</PrimaryTabPanel>

			<PrimaryTabPanel value={selectedTab} id='alerts' key={2} >
				<BatteryAlerts batteryID={batteryID} />
			</PrimaryTabPanel>

			<PrimaryTabPanel value={selectedTab} id='performance' key={3} >
				<BatteryPerformance batteryID={batteryID} />
			</PrimaryTabPanel>

			<PrimaryTabPanel value={selectedTab} id='health' key={4} >
				<BatteryHealth batteryID={batteryID} />
			</PrimaryTabPanel>

			{hasBatteryIntelligence && (
				<PrimaryTabPanel value={selectedTab} id='batteryHealth' key={7} >
					<BatteryHealthV2 batteryID={batteryID} />
				</PrimaryTabPanel>
			)}

			<PrimaryTabPanel value={selectedTab} id='vehicleVitals' key={5} >
				<VehicleVitals batteryID={batteryID} />
			</PrimaryTabPanel>

			<PrimaryTabPanel value={selectedTab} id='deviceHealth' key={6} >
				<DeviceHealth batteryID={batteryID} />
			</PrimaryTabPanel>

			{hasBatteryComponents && (
				<PrimaryTabPanel value={selectedTab} id='components' key={8} >
					<Components batteryID={batteryID} />
				</PrimaryTabPanel>
			)}
		</div>
	);
}

export default DigitalTwinMain;
